import React from 'react';
import { parseReminderHours, parseReminderMinutes, toTotalMinutes } from '../utils';

// Base interface with shared fields
interface BaseFormValues {
  title: string;
  description: string;
  dueDate: string;
  dueTime: string;
  recurrence: string;
  reminderOffsetMinutes: string;
}

interface TaskFormProps<T extends BaseFormValues> {
  values: T;
  setValues: React.Dispatch<React.SetStateAction<T>>;
  onSave: () => void;
  onCancel: () => void;
  isEditing?: boolean;
  hasChanges?: boolean;
  showReminder?: boolean;
}

function TaskForm<T extends BaseFormValues>({
  values,
  setValues,
  onSave,
  onCancel,
  isEditing = false,
  hasChanges = false,
  showReminder = true,
}: TaskFormProps<T>) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSave();
    }
  };

  const updateField = <K extends keyof BaseFormValues>(field: K, value: BaseFormValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="card-edit-form" onKeyDown={handleKeyDown}>
      <input
        type="text"
        className="card-title-edit"
        placeholder="Task title*"
        value={values.title}
        onChange={(e) => updateField('title', e.target.value)}
        required
      />

      <textarea
        className="card-description-edit"
        placeholder="Add description..."
        value={values.description}
        onChange={(e) => updateField('description', e.target.value)}
        rows={3}
      />

      <div className="card-date-edit-wrapper">
        <input
          type="date"
          className="card-date-edit"
          value={values.dueDate}
          onChange={(e) => updateField('dueDate', e.target.value)}
          onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
        />
        <input
          type="time"
          className="card-time-edit"
          value={values.dueTime}
          onChange={(e) => updateField('dueTime', e.target.value)}
          placeholder="HH:mm"
        />
        {showReminder && (
          <div className="card-reminder-inline">
            <input
              type="number"
              className="card-reminder-hours"
              min={0}
              value={parseReminderHours(values.reminderOffsetMinutes)}
              onChange={(e) => {
                const newHours = e.target.value;
                const mins = parseReminderMinutes(values.reminderOffsetMinutes);
                const total = toTotalMinutes(newHours, mins);
                updateField('reminderOffsetMinutes', total);
              }}
              placeholder="0"
            />
            <span className="reminder-label">hr</span>
            <input
              type="number"
              className="card-reminder-mins"
              min={0}
              max={59}
              value={parseReminderMinutes(values.reminderOffsetMinutes)}
              onChange={(e) => {
                const newMins = e.target.value;
                const hrs = parseReminderHours(values.reminderOffsetMinutes);
                const total = toTotalMinutes(hrs, newMins);
                updateField('reminderOffsetMinutes', total);
              }}
              placeholder="0"
            />
            <span className="reminder-label">min before</span>
            <button
              className="reminder-clear"
              onClick={() => updateField('reminderOffsetMinutes', '')}
              title="Clear reminder"
              type="button"
            >
              ×
            </button>
          </div>
        )}
      </div>

      <select
        className="card-recurrence-edit"
        value={values.recurrence}
        onChange={(e) => updateField('recurrence', e.target.value)}
      >
        <option value="">No recurrence</option>
        <option value="daily">Daily</option>
        <option value="weekly">Weekly</option>
        <option value="biweekly">Bi-weekly</option>
        <option value="monthly">Monthly</option>
        <option value="quarterly">Quarterly</option>
        <option value="yearly">Yearly</option>
      </select>

      <div className="card-edit-actions">
        <button
          className={`save-button ${isEditing && hasChanges ? 'has-changes' : ''}`}
          onClick={onSave}
        >
          Save
        </button>
        <button className="cancel-button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default TaskForm;
